/**
 * Platform detection helpers for Play Store billing compliance
 * and premium status checks.
 */

declare global {
  interface Window {
    Android?: {
      openSubscription?: () => void;
      isPremium?: () => boolean;
    };
    __hydroPremiumVerified?: boolean;
    median?: unknown;
    gonern?: unknown;
  }
}

/**
 * Detects if the app is running inside an Android WebView / Median wrapper.
 * Used for Play Store billing compliance — hides external payment links.
 */
export const isAndroidApp = (): boolean => {
  if (typeof window === "undefined") return false;

  // Persist source=pwa to localStorage on first visit so it survives navigation
  const params = new URLSearchParams(window.location.search);
  if (params.get("source") === "pwa" || params.get("app") === "true") {
    try { localStorage.setItem("source", "pwa"); } catch {}
    return true;
  }

  // Read persisted flag (survives navigation / page reloads)
  try {
    if (localStorage.getItem("source") === "pwa") return true;
  } catch {}

  // Standalone display mode (PWA installed to home screen)
  if (window.matchMedia("(display-mode: standalone)").matches) {
    try { localStorage.setItem("source", "pwa"); } catch {}
    return true;
  }

  // Check navigator.standalone (iOS)
  if ((navigator as any).standalone === true) {
    try { localStorage.setItem("source", "pwa"); } catch {}
    return true;
  }

  // Check Median native bridge
  if (window.median || window.gonern) return true;

  // Check Android WebView UA string
  const ua = window.navigator.userAgent;
  if (ua.includes("wv") || ua.includes("Median")) return true;

  // Check Android bridge object
  if (window.Android !== undefined) return true;

  return false;
};

/**
 * Opens the Android native subscription management screen.
 * No-op on web.
 */
export const openAndroidSubscription = (): void => {
  window.Android?.openSubscription?.();
};

/**
 * Checks premium status from native Android bridge or verified flag.
 * This is a supplementary check — the primary source of truth is PremiumContext.
 */
export const isNativePremium = (): boolean => {
  if (window.Android?.isPremium?.()) return true;
  if (window.__hydroPremiumVerified === true) return true;
  return false;
};
